"use client";
import { ComponentProps, useEffect, useState } from "react";
import cn from "classnames";
import {
  Popover,
  PopoverBackdrop,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from "@headlessui/react";
import { getDataAutomationId, getDataLinkAttributes } from "@/helpers";
import Image from "next/image";
import Link from "@/components/Link";
import { GlobalNavigationProps } from "../GlobalNavigation";
import SkipToMainBtn from "../SkipToMainBtn";
import { getHrefFromCustomUrlPlugin } from "@/helpers";
import HeaderNavigationPanel from "./HeaderNavigationPanel";
import ConsolidatedDropdown from "./ConsolidatedDropdown";
import { CaretDownSolid, Globe, ProfileIcon } from "@/icons";
import ConsolidatedSearchBar from "./ConsolidatedSearchBar";
import { useIsPageOnTop } from "@/hooks";

export type ConsolidatedNavigationProps = Pick<
  GlobalNavigationProps,
  | "superHeader"
  | "menu"
  | "locale"
  | "asPath"
  | "contactLink"
  | "loginMenu"
  | "sticky"
  | "isDocked"
>;

const ConsolidatedNavigation: React.FC<ConsolidatedNavigationProps> = ({
  sticky,
  superHeader,
  menu,
  locale,
  asPath,
  contactLink,
  loginMenu,
  isDocked,
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const isPageOnTop = useIsPageOnTop(sticky);
  const isPageOnTopDocked = useIsPageOnTop(isDocked);

  const currentLanguage = superHeader?.languageSwitcher?.languages?.find(
    (lang) => lang.languageCode === locale,
  ) || {
    languageCode: "en-us",
    ariaLabel: "English",
  };

  const navItemClass =
    "group data-focus:ring-swdc-focus-primary transition-all data-focus:ring-2 rounded-sm data-focus:ring-offset-1 after:bg-swdc-orange-400 text-link-xs xl:text-link-s text-swdc-black-600 active:text-swdc-blue-500 after:rounded-corner-pill relative block text-center outline-none after:absolute after:right-0 after:bottom-0 after:left-0 after:h-0 after:content-[''] hover:after:h-0.5 active:after:h-0.5";

  const iconBtnClass =
    "group data-focus:ring-swdc-focus-primary after:bg-swdc-orange-400 after:rounded-corner-pill relative flex items-center justify-center rounded-sm px-0.5 pt-0.5 pb-1.5 outline-none after:absolute after:right-0 after:bottom-0 after:left-0 after:h-0 after:content-[''] hover:after:h-0.5 active:after:h-0.5 data-focus:ring-1 data-focus:ring-offset-1";

  const dropdownBtnClass =
    "group data-focus:ring-swdc-focus-primary text-link-xs flex w-fit items-center gap-x-2 rounded-xs whitespace-nowrap outline-none data-focus:ring-1 data-focus:ring-offset-1";

  const dropdownElementClass =
    "text-swdc-black-600 group-hover:border-b-swdc-black-600 group-active:border-b-swdc-blue-500 group-active:text-swdc-blue-500 border-b border-transparent";
  const [isPageWithSecondary, setIsPageWithSecondary] = useState(false);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (document.getElementById("secondaryNav")) {
        setIsPageWithSecondary(true);
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);
  const isDockedSticky = isDocked && !isPageOnTopDocked && !isPageWithSecondary;
  
  const isDockedNotSticky = isDocked && (isPageOnTopDocked || isPageWithSecondary);

  return (
    <div
      className={cn(
        isPageOnTop && !isDocked && "bg-white",
        isDockedNotSticky &&
          "border-swdc-gray-200 h-19 border-b bg-white xl:h-22",
      )}
      id={isDocked ? "dockedNav" : "consolidatedNav"}
    >
      <nav
        className={cn(
          "mx-auto flex max-w-7xl flex-col rounded-xl bg-white px-4 lg:px-6",
          sticky && !isPageOnTop && "shadow-xs/30",
          !isDocked && "h-20",
          isDockedSticky && "mt-2 shadow-xs/30",
        )}
        aria-label="Primary"
      >
        <SkipToMainBtn
          isConsolidatedNavigation
          text={superHeader.skipToMainContentLabel}
        />
        <div
          className={cn(
            "flex items-center justify-between gap-x-8",
            !isDocked && "my-auto pt-1",
            isDockedNotSticky && "mt-2 mb-1.75 h-15 xl:h-18",
            isDockedSticky && "my-3 h-9 xl:h-12",
          )}
        >
          <div
            className={cn(
              "w-full max-w-28.75 shrink-0 xl:max-w-36.75",
              isDocked && "xl:mr-1.5",
            )}
          >
            {menu?.logo}
          </div>

          {!isSearchOpen && (
            <PopoverGroup
              as="ul"
              className={cn("flex min-w-0 shrink items-center", {
                "gap-1.5 xl:gap-8":
                  locale?.startsWith("es") || locale?.startsWith("de"),
                "gap-1 xl:gap-6": locale?.startsWith("ja"),
                "gap-2 xl:gap-7":
                  locale?.startsWith("fr") || locale?.startsWith("pt"),
                "gap-4 xl:gap-8":
                  !locale?.startsWith("de") &&
                  !locale?.startsWith("es") &&
                  !locale?.startsWith("ja") &&
                  !locale?.startsWith("fr") &&
                  !locale?.startsWith("pt") &&
                  !isDocked,
                "gap-3.5 xl:gap-7":
                  !locale?.startsWith("de") &&
                  !locale?.startsWith("es") &&
                  !locale?.startsWith("ja") &&
                  !locale?.startsWith("fr") &&
                  !locale?.startsWith("pt") &&
                  isDocked,
              })}
            >
              {menu?.items?.map((item, index) => {
                return (
                  <li key={`${item.title}-${index}`}>
                    {item.href && item.title ? (
                      <Link
                        className={cn(
                          navItemClass,
                          "focus-visible:ring-swdc-focus-primary py-2 focus-visible:ring-2 focus-visible:ring-offset-1",
                          isDocked ? "font-geist" : "xl:font-medium",
                        )}
                        href={getHrefFromCustomUrlPlugin(item.href)}
                        {...getDataLinkAttributes({
                          dlt: item.dlt,
                          dld: item.dld,
                        })}
                        {...getDataAutomationId(`navigation-item-${index}`)}
                      >
                        {item.title}
                      </Link>
                    ) : (
                      <Popover>
                        <PopoverButton
                          className={({ open }) =>
                            cn(
                              navItemClass,
                              "py-2 pr-px",
                              isDocked ? "font-geist" : "xl:font-medium",
                              open &&
                                "after:h-0.75! hover:after:h-0.5! [&_svg]:rotate-180",
                            )
                          }
                          {...getDataLinkAttributes({
                            dld: item.dld,
                            dlt: item.dlt,
                          })}
                          {...getDataAutomationId(
                            `navigation-dropdown-${index}`,
                          )}
                        >
                          <span className="pr-0.75">{item.title}</span>
                          <CaretDownSolid
                            className="text-swdc-black-600 group-active:text-swdc-blue-500 inline-block h-2 w-2 shrink-0 align-middle"
                            aria-hidden="true"
                          />
                        </PopoverButton>
                        <PopoverBackdrop className="bg-swdc-cool-gray-200/30 fixed inset-0 -z-10 backdrop-blur" />
                        <PopoverPanel
                          className="absolute left-1/2 z-999 mt-5.5 w-full max-w-308 -translate-x-1/2"
                          unmount={false}
                        >
                          {({ close }) => (
                            <HeaderNavigationPanel
                              {...item}
                              panelColor={
                                {
                                  coolGray: "cool-gray-700",
                                  blue: "blue-500",
                                  gray: "gray-500",
                                }[menu.colorVariant] as ComponentProps<
                                  typeof HeaderNavigationPanel
                                >["panelColor"]
                              }
                              index={index}
                              closeMenu={close}
                            />
                          )}
                        </PopoverPanel>
                      </Popover>
                    )}
                  </li>
                );
              })}
            </PopoverGroup>
          )}

          <div
            className={cn(
              "flex max-h-9 flex-1 shrink-0 items-center justify-end",
              {
                "gap-1.5 xl:gap-x-3":
                  locale?.startsWith("es") || locale?.startsWith("de"),
                "gap-1 xl:gap-x-3":
                  locale?.startsWith("ja") || locale?.startsWith("fr"),
                "gap-x-3":
                  !locale?.startsWith("es") &&
                  !locale?.startsWith("ja") &&
                  !locale?.startsWith("fr") &&
                  !locale?.startsWith("de"),
              },
            )}
          >
            {superHeader?.search && (
              <ConsolidatedSearchBar
                search={superHeader.search}
                isSearchOpen={isSearchOpen}
                setIsSearchOpen={setIsSearchOpen}
                locale={locale}
              />
            )}
            {(superHeader.languageSwitcher?.languages?.length || 0) > 1 && (
              <ConsolidatedDropdown
                ariaLabel={
                  superHeader?.languageSwitcher?.languageButtonLabel
                    ? superHeader?.languageSwitcher?.languageButtonLabel?.replace(
                        "{{lang}}",
                        currentLanguage.ariaLabel,
                      )
                    : `Current language ${currentLanguage.ariaLabel}, Change Language`
                }
                icon={<Globe className="text-swdc-black-600 size-5" />}
                dld={superHeader?.languageSwitcher?.languageButtonDLD}
                dlt={superHeader?.languageSwitcher?.languageButtonDLT}
                automationId="language-switcher"
                buttonClassName={iconBtnClass}
              >
                {superHeader?.languageSwitcher?.languages
                  ?.filter(
                    (lang) =>
                      lang.languageCode !== currentLanguage.languageCode,
                  )
                  ?.map(
                    ({
                      icon,
                      ariaLabel,
                      languageCode,
                      title,
                      href,
                      dld,
                      dlt,
                    }) => (
                      <li key={languageCode}>
                        <PopoverButton
                          aria-label={ariaLabel}
                          as={Link}
                          className={dropdownBtnClass}
                          href={`${languageCode === "en-us" ? "" : `/${href}`}${asPath}`}
                          locale={languageCode}
                          dld={dld}
                          dlt={dlt}
                        >
                          {icon && (
                            <Image
                              src={icon}
                              alt={ariaLabel + " icon"}
                              height={20}
                              width={20}
                              aria-hidden={true}
                              className="size-5 max-w-none rounded-2xl"
                            />
                          )}
                          <span
                            className={dropdownElementClass}
                            lang={languageCode}
                          >
                            {title}
                          </span>
                        </PopoverButton>
                      </li>
                    ),
                  )}
              </ConsolidatedDropdown>
            )}
            {loginMenu?.links && loginMenu.links?.length > 0 && (
              <ConsolidatedDropdown
                ariaLabel={loginMenu.ariaLabel || "Login options"}
                icon={
                  <ProfileIcon
                    aria-hidden={true}
                    className="text-swdc-black-600 size-5"
                  />
                }
                dld={loginMenu.dld}
                dlt={loginMenu.dlt}
                automationId="login-menu"
                buttonClassName={iconBtnClass}
                opensOnHover={true}
              >
                {loginMenu.links.map((link, index) => (
                  <li key={`${link.title}-${index}`}>
                    <PopoverButton
                      as={Link}
                      className={dropdownBtnClass}
                      href={getHrefFromCustomUrlPlugin(link.href)}
                      dld={link.dld}
                      dlt={link.dlt}
                      {...getDataAutomationId(`login-link-${index}`)}
                    >
                      <span className={dropdownElementClass}>{link.title}</span>
                    </PopoverButton>
                  </li>
                ))}
              </ConsolidatedDropdown>
            )}
            {contactLink?.title && contactLink?.href && (
              <Link
                className={cn(
                  navItemClass,
                  "focus-visible:ring-swdc-focus-primary shrink-0 py-1 focus-visible:ring-1 focus-visible:ring-offset-1",
                  isDocked ? "font-geist font-semi-bold" : "font-bold",
                )}
                href={getHrefFromCustomUrlPlugin(contactLink.href)}
                {...getDataLinkAttributes({
                  dlt: contactLink.dlt,
                  dld: contactLink.dld,
                })}
                {...getDataAutomationId("contact-link")}
              >
                {contactLink.title}
              </Link>
            )}
            <div
              className={cn("flex shrink-0 flex-row", {
                "pl-3":
                  !locale?.startsWith("es") &&
                  !locale?.startsWith("ja") &&
                  !locale?.startsWith("fr") &&
                  !locale?.startsWith("pt") &&
                  !isDocked,
                "pl-2 xl:pl-3":
                  !locale?.startsWith("es") &&
                  !locale?.startsWith("ja") &&
                  !locale?.startsWith("fr") &&
                  !locale?.startsWith("pt") &&
                  isDocked,
              })}
            >
              {menu.cta}
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default ConsolidatedNavigation;
